import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageShell } from './layout';
import { GAME_CONFIG } from '../games';
import { getCurrentDate, toDateFormat, GAME_STATUS } from '../util';
import { darkenColor, isLightColor } from '../utils/colors';
import { useGameStatuses } from '../contexts/GameStatusesContext';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import styles from '../styles/GameArchivePage.module.css';
import FaIcon from './FaIcon';

// Italian day names - Sunday first (domenica on the left)
const DAYS_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const DAYS_FULL_IT = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// Italian month names
const MONTHS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

// Generate dates for a specific month in calendar format (with empty slots for alignment)
const generateMonthDates = (year, month) => {
  // First day of the month
  const firstDay = new Date(year, month, 1);
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);
  
  const calendarDays = [];
  
  // Add empty slots for days before the first of the month
  // getDay() returns 0 for Sunday, which is our first column
  const startDayOfWeek = firstDay.getDay();
  for (let i = 0; i < startDayOfWeek; i++) {
    calendarDays.push(null); // Empty slot
  }
  
  // Add all days of the month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    calendarDays.push(toDateFormat(date));
  }
  
  // Pad with trailing empty slots so every row has exactly 7 items (consistent grid layout)
  const totalSlots = calendarDays.length;
  const remainder = totalSlots % 7;
  if (remainder > 0) {
    for (let i = 0; i < 7 - remainder; i++) {
      calendarDays.push(null);
    }
  }
  
  return {
    dates: calendarDays,
    monthName: MONTHS_IT[month],
    year: year,
    // Helper for date range queries
    startDate: toDateFormat(firstDay),
    endDate: toDateFormat(lastDay)
  };
};

const GameArchivePage = () => {
  const location = useLocation();
  const currentDate = getCurrentDate();
  
  // Get puzzle instances context for lazy loading
  const { getPuzzleInstanceId, fetchPuzzleInstancesForRange } = usePuzzleInstances();

  // Extract game path from URL (e.g., /collegamenti/archivio -> collegamenti)
  const gamePath = location.pathname.split('/').filter(Boolean)[0];

  // Find the game config by pathName
  const gameConfig = useMemo(() => {
    return Object.values(GAME_CONFIG).find(g => g.pathName === gamePath);
  }, [gamePath]);

  // Initialize to current month
  const today = new Date();
  const [displayMonth, setDisplayMonth] = useState(today.getMonth());
  const [displayYear, setDisplayYear] = useState(today.getFullYear());
  
  // Track which months we've loaded puzzle instances for
  const loadedMonthsRef = useRef(new Set());
  const [isLoadingMonthInstances, setIsLoadingMonthInstances] = useState(false);
  // Track puzzle instance availability for displayed month  
  const [monthPuzzleInstances, setMonthPuzzleInstances] = useState({});

  // Generate calendar data for the selected month
  const calendarData = useMemo(() => 
    generateMonthDates(displayYear, displayMonth), 
    [displayYear, displayMonth]
  );
  
  // Check if we're viewing the current month
  const isCurrentMonth = displayMonth === today.getMonth() && displayYear === today.getFullYear();

  // If game not found, redirect to home
  if (!gameConfig) {
    return null; // Will be handled by NotFound route
  }

  const {
    displayName,
    backgroundColor,
    icon: GameIcon,
    id: gameId
  } = gameConfig;
  
  // Load puzzle instances for the displayed month
  useEffect(() => {
    const monthKey = `${gameId}:${displayYear}-${displayMonth}`;
    
    // Skip if already loaded
    if (loadedMonthsRef.current.has(monthKey)) {
      return;
    }
    
    const loadMonthInstances = async () => {
      setIsLoadingMonthInstances(true);
      
      try {
        const instances = await fetchPuzzleInstancesForRange(
          gameId,
          calendarData.startDate,
          calendarData.endDate
        );
        
        setMonthPuzzleInstances(prev => ({
          ...prev,
          ...instances
        }));
        
        loadedMonthsRef.current.add(monthKey);
      } catch (err) {
        console.error('Error loading month instances:', err);
      } finally {
        setIsLoadingMonthInstances(false);
      }
    };
    
    loadMonthInstances();
  }, [gameId, displayYear, displayMonth, calendarData.startDate, calendarData.endDate, fetchPuzzleInstancesForRange]);

  // Navigate to previous month
  const goToPreviousMonth = useCallback(() => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  }, [displayMonth, displayYear]);

  // Navigate to next month (only if not current month)
  const goToNextMonth = useCallback(() => {
    if (isCurrentMonth) return;
    
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  }, [displayMonth, displayYear, isCurrentMonth]);

  // Check if a date has a puzzle instance
  const hasPuzzleForDate = useCallback((date) => {
    // Check local cache from monthly load first
    if (monthPuzzleInstances[date]) return true;
    // Fall back to global puzzle instances context
    return !!getPuzzleInstanceId(gameId, date);
  }, [monthPuzzleInstances, getPuzzleInstanceId, gameId]);

  // Prepare game data for useGameStatuses hook (only dates with puzzle instances)
  const gameDataForStatuses = useMemo(() => {
    const validDates = calendarData.dates.filter(date => date !== null && hasPuzzleForDate(date));
    return [{
      gameId: gameId,
      games: validDates.map(date => ({
        date,
        gameId: gameId,
        pathName: gameConfig.pathName,
        backgroundColor: backgroundColor,
      }))
    }];
  }, [gameId, gameConfig.pathName, backgroundColor, calendarData.dates, hasPuzzleForDate]);

  // Fetch server statuses (for all users - anonymous users will get null/not_started)
  const archiveScopeKey = useMemo(
    () => `archive:${gameId}:${displayYear}-${String(displayMonth + 1).padStart(2, '0')}`,
    [gameId, displayYear, displayMonth],
  );

  const { getGameStatus: getServerGameStatus, isLoading: isLoadingStatuses } = useGameStatuses(gameDataForStatuses, true, {
    scopeKey: archiveScopeKey,
    priority: 'medium',
    subscribeOnly: false,
  });

  const isDarkText = isLightColor(backgroundColor);
  const darkerBg = darkenColor(backgroundColor, 15);
  const archiveNavigationState = useMemo(() => ({ auth_origin: 'game_archive' }), []);

  // Get status icon for a game date
  const getStatusIcon = (status) => {
    if (status === GAME_STATUS.FINISHED) return '✓';
    if (status === GAME_STATUS.IN_PROGRESS) return '...';
    return null;
  };

  return (
    <PageShell>
      {/* Header Section */}
      <section 
        className={`${styles.headerSection} ${isDarkText ? styles.darkText : ''}`}
        style={{ backgroundColor }}
      >
        <div className={styles.headerContent}>
          <div className={styles.iconWrapper} style={{ backgroundColor: darkerBg }}>
            {GameIcon && <GameIcon size={32} />}
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Archivio {displayName}</h1>
            {/* Month Navigation */}
            <div className={styles.monthNav}>
              <button 
                className={styles.monthNavButton}
                onClick={goToPreviousMonth}
                aria-label="Mese precedente"
              >
                <FaIcon className="fas fa-chevron-left"  />
              </button>
              <span className={styles.monthNavLabel}>
                {calendarData.monthName} {calendarData.year}
              </span>
              <button 
                className={`${styles.monthNavButton} ${isCurrentMonth ? styles.monthNavButtonDisabled : ''}`}
                onClick={goToNextMonth}
                disabled={isCurrentMonth}
                aria-label="Mese successivo"
              >
                <FaIcon className="fas fa-chevron-right"  />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className={styles.calendarSection}>
        <div className={styles.calendarContainer}>
          {/* Day headers */}
          <div className={styles.dayHeaders}>
            {DAYS_IT.map((day, index) => (
              <div key={day} className={styles.dayHeader}>
                <span className={styles.dayShort}>{day}</span>
                <span className={styles.dayFull}>{DAYS_FULL_IT[index]}</span>
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className={styles.calendarGrid}>
            {calendarData.dates.map((date, index) => {
              if (!date) {
                // Empty slot
                return <div key={`empty-${index}`} className={styles.emptySlot} />;
              }

              const dateObj = new Date(date + 'T00:00:00');
              const dayNum = dateObj.getDate();
              const isToday = date === currentDate;
              const isFuture = date > currentDate;
              const hasPuzzle = hasPuzzleForDate(date);
              
              const targetPath = `/${gameConfig.pathName}/${date}`;

              // Show loading skeleton with diagonal flash while loading month instances or statuses
              const showLoading = (isLoadingMonthInstances || isLoadingStatuses) && !isFuture;

              // Always use server status (null means not_started)
              const serverStatus = getServerGameStatus(gameId, date);
              const status = serverStatus ?? GAME_STATUS.NOT_STARTED;
              const statusIcon = getStatusIcon(status);

              // Future dates are disabled (no loading indicator)
              // Use Link (same element type as other cards) for consistent sizing - divs render larger than anchors in grid
              if (isFuture) {
                return (
                  <Link
                    key={date}
                    to="#"
                    onClick={(e) => e.preventDefault()}
                    className={`${styles.calendarCard} ${styles.calendarCardFuture}`}
                    style={{ '--card-bg': backgroundColor }}
                    aria-disabled="true"
                  >
                    <span className={styles.calendarDate}>{dayNum}</span>
                  </Link>
                );
              }

              // Dates without puzzles (no puzzle instance exists)
              // Use Link (same element type as other cards) for consistent sizing - divs render larger than anchors in grid
              if (!hasPuzzle && !isLoadingMonthInstances) {
                return (
                  <Link
                    key={date}
                    to="#"
                    onClick={(e) => e.preventDefault()}
                    className={`${styles.calendarCard} ${styles.calendarCardNoPuzzle}`}
                    style={{ '--card-bg': backgroundColor }}
                  >
                    <span className={styles.calendarDate}>{dayNum}</span>
                  </Link>
                );
              }

              // Regular accessible date (with loading indicator if loading)
              return (
                <Link
                  key={date}
                  to={targetPath}
                  state={archiveNavigationState}
                  className={`${styles.calendarCard} ${isToday ? styles.calendarCardToday : ''} ${status === GAME_STATUS.FINISHED ? styles.calendarCardCompleted : ''} ${status === GAME_STATUS.IN_PROGRESS ? styles.calendarCardInProgress : ''} ${showLoading ? styles.calendarCardLoading : ''} ${isDarkText ? styles.calendarCardLightBrand : ''}`}
                  style={{ '--card-bg': backgroundColor }}
                >
                  <span className={styles.calendarDate}>{dayNum}</span>
                  {statusIcon && (
                    <span className={styles.statusBadge}>
                      {statusIcon}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Legend - mirrors calendar card structure (status badge in corner) */}
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <span className={styles.legendCard} style={{ backgroundColor }} />
              <span>Non giocato</span>
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendCard} ${styles.legendCardInProgress}`} style={{ backgroundColor }}>
                <span className={styles.legendBadge}>...</span>
              </span>
              <span>In corso</span>
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendCard} ${styles.legendCardCompleted} ${isDarkText ? styles.legendCardLightBrand : ''}`} style={{ backgroundColor }}>
                <span className={styles.legendBadge}>✓</span>
              </span>
              <span>Completato</span>
            </div>
            <div className={styles.legendItem}>
              <span className={styles.legendCardNoPuzzle} />
              <span>Non disponibile</span>
            </div>

          </div>
        </div>

        {/* CTA */}
        <div className={styles.ctaSection}>
          <Link 
            to={`/${gameConfig.pathName}/${currentDate}`}
            state={archiveNavigationState}
            className={`${styles.ctaButton} ${isDarkText ? styles.ctaButtonDarkText : ''}`}
            style={{ backgroundColor }}
          >
            <span>Partita di oggi</span>
            <FaIcon className={`fas fa-arrow-right ${styles.ctaIcon}`}  />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default GameArchivePage;
